import { filter as filterLodash, get, mapValues } from 'lodash'
import { createSelector, OutputSelector } from 'reselect'

import { AnyObject } from '../types'

type TOutputSelectorAnyObjectArray = OutputSelector<any, AnyObject[], any>
type TOutputSelectorAnyObject = OutputSelector<any, AnyObject, any>
export interface ISelectorProps {
  root: any
  selectState: any
  selectFilterMode: OutputSelector<any, string, any>
  selectItemsById: OutputSelector<any, string[], any>
  selectItemsByIndex: OutputSelector<any, string[], any>
  selectLoadedPages: OutputSelector<any, boolean, any>
  selectIsLoading: OutputSelector<any, boolean, any>
  selectTotalCount: OutputSelector<any, number, any>
  selectFilter: TOutputSelectorAnyObjectArray
  selectIncludedFilters: TOutputSelectorAnyObjectArray
  selectFilterForm: TOutputSelectorAnyObjectArray
  selectUi: TOutputSelectorAnyObjectArray
  selectSortableColumns: TOutputSelectorAnyObjectArray
  selectItems: TOutputSelectorAnyObject
  selectFilterClean: TOutputSelectorAnyObjectArray
  selectSort: TOutputSelectorAnyObjectArray
  selectItemsToRestore: TOutputSelectorAnyObjectArray
  selectUseLocalStateFiltering: TOutputSelectorAnyObjectArray
  selectGroupedItems: TOutputSelectorAnyObjectArray
  selectCurrentRequestId: OutputSelector<any, number, any>
  selectInitialFilters: TOutputSelectorAnyObjectArray
  selectCustomData: TOutputSelectorAnyObject
  selectCustomHeaders: TOutputSelectorAnyObjectArray
  selectCustomPayload: TOutputSelectorAnyObjectArray
  selectFulltextSearchLoading: OutputSelector<any, boolean, any>
  selectAllIds: TOutputSelectorAnyObjectArray
}

export interface ISelectorStructuredProps {
  filterMode: OutputSelector<any, string, any>
  itemsById: TOutputSelectorAnyObject
  itemsByIndex: TOutputSelectorAnyObject
  loadedPages: OutputSelector<any, boolean, any>
  isLoading: OutputSelector<any, boolean, any>
  totalCount: OutputSelector<any, number, any>
  filter: TOutputSelectorAnyObjectArray
  includedFilters: TOutputSelectorAnyObjectArray
  filterForm: TOutputSelectorAnyObject
  ui: TOutputSelectorAnyObject
  sortableColumns: TOutputSelectorAnyObjectArray
  items: TOutputSelectorAnyObject
  filterClean: TOutputSelectorAnyObjectArray
  sort: TOutputSelectorAnyObjectArray
  useLocalStateFiltering: TOutputSelectorAnyObject
  groupedItems: TOutputSelectorAnyObject
  currentRequestId: OutputSelector<any, number, any>
  initialFilter: TOutputSelectorAnyObjectArray
  customData: TOutputSelectorAnyObject
  customHeaders: TOutputSelectorAnyObjectArray
  fulltextSearchLoading: OutputSelector<any, boolean, any>
  allIds: TOutputSelectorAnyObjectArray
}

type TRoot = {
  [key: string]: any
}

export function generateSelectors(id: string): ISelectorProps {
  const root = (s: TRoot): any => s[id]

  const selectState = root

  const selectCurrentRequestId = createSelector(
    root,
    (s) => s.currentRequestId,
  ) as any

  const selectFilterMode = createSelector(root, (s) => s.filterMode) as any

  const selectItemsById = createSelector(root, (s) => s.itemsById) as any

  const selectItemsByIndex = createSelector(root, (s) => s.itemsByIndex) as any

  const selectLoadedPages = createSelector(root, (s) => s.loadedPages) as any

  const selectIsLoading = createSelector(root, (s) =>
    s.fulltextSearchLoading ? false : s.isLoading,
  ) as any

  const selectTotalCount = createSelector(root, (s) => s.totalCount) as any

  const selectFilter = createSelector(root, (s) => s.filter) as any

  const selectIncludedFilters = createSelector(
    root,
    (s) => s.includedFilters,
  ) as any

  const selectFilterForm = createSelector(root, (s) => s.filterForm) as any

  const selectInitialFilters = createSelector(
    root,
    (s) => s.initialFilter,
  ) as any

  const selectUi = createSelector(root, (s) => s.ui) as any

  const selectSortableColumns = createSelector(
    root,
    (s) => s.sortableColumns,
  ) as any

  const selectItems = createSelector(
    [selectItemsById, selectItemsByIndex],
    (itemsById, itemsByIndex: any) =>
      mapValues(itemsByIndex, (id, index) => get(itemsById, id)),
  ) as any

  const selectFilterClean = createSelector(selectFilterForm, (filter = {}) => {
    return {
      ...filter,
      filter: filterLodash(
        get(filter, 'filter', []) || [],
        (filter) => filter.value !== '',
      ),
    }
  }) as any

  const selectSort = createSelector(
    selectFilterForm,
    (filter: any) => filter.sort,
  ) as any

  const selectItemsToRestore = createSelector(
    root,
    (s) => s.itemsToRestoreFrom,
  ) as any

  const selectUseLocalStateFiltering = createSelector(
    root,
    (s) => s.useLocalStateFiltering,
  ) as any

  const selectCustomHeaders = createSelector(
    root,
    (s) => s.customHeaders,
  ) as any

  const selectCustomPayload = createSelector(
    root,
    (s) => s.customPayload,
  ) as any

  const selectCustomData = createSelector(root, (s) => s.customData) as any
  const selectGroupedItems = createSelector(root, (s) => s.groupedItems) as any
  const selectFulltextSearchLoading = createSelector(
    root,
    (s) => s.fulltextSearchLoading,
  ) as any

  const selectAllIds = createSelector(root, (s) => s.allIds || []) as any

  return {
    root,
    selectState,
    selectFilterMode,
    selectItemsById,
    selectItemsByIndex,
    selectLoadedPages,
    selectIsLoading,
    selectTotalCount,
    selectFilter,
    selectIncludedFilters,
    selectFilterForm,
    selectUi,
    selectSortableColumns,
    selectItems,
    selectFilterClean,
    selectSort,
    selectItemsToRestore,
    selectUseLocalStateFiltering,
    selectGroupedItems,
    selectCurrentRequestId,
    selectInitialFilters,
    selectCustomData,
    selectCustomHeaders,
    selectCustomPayload,
    selectFulltextSearchLoading,
    selectAllIds,
  }
}

export function generateStructuredSelector(
  selectors: ISelectorProps,
): ISelectorStructuredProps {
  return {
    filterMode: selectors.selectFilterMode,
    itemsById: selectors.selectItemsById,
    itemsByIndex: selectors.selectItemsByIndex,
    loadedPages: selectors.selectLoadedPages,
    isLoading: selectors.selectIsLoading,
    totalCount: selectors.selectTotalCount,
    filter: selectors.selectFilter,
    includedFilters: selectors.selectIncludedFilters,
    filterForm: selectors.selectFilterForm,
    ui: selectors.selectUi,
    sortableColumns: selectors.selectSortableColumns,
    items: selectors.selectItems,
    filterClean: selectors.selectFilterClean,
    sort: selectors.selectSort,
    useLocalStateFiltering: selectors.selectUseLocalStateFiltering,
    groupedItems: selectors.selectGroupedItems,
    currentRequestId: selectors.selectCurrentRequestId,
    initialFilter: selectors.selectInitialFilters,
    customData: selectors.selectCustomData,
    customHeaders: selectors.selectCustomHeaders,
    fulltextSearchLoading: selectors.selectFulltextSearchLoading,
    allIds: selectors.selectAllIds,
  }
}
